import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, Send, Heart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import {getAuthHeaders, hasToken, BACKEND_URL} from "@/lib/api";

const API = `${BACKEND_URL}/api`;

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

export default function EventGuestbook({ eventId, isOwner }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEntry, setNewEntry] = useState({ name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, [eventId]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/guestbook`);
      setEntries(response.data || []);
    } catch (error) {
      console.error("Failed to fetch guestbook:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEntry.name.trim() || !newEntry.message.trim()) {
      toast.error("Bitte Name und Nachricht ausfüllen");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/events/${eventId}/guestbook`, {
        author_name: newEntry.name,
        message: newEntry.message
      });
      setNewEntry({ name: "", message: "" });
      toast.success("Eintrag hinzugefügt! 📝");
      fetchEntries();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/guestbook/${entryId}`, authConfig());
      toast.success("Eintrag gelöscht");
      fetchEntries();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const likeEntry = async (entryId) => {
    try {
      await axios.post(`${API}/events/${eventId}/guestbook/${entryId}/like`, {});
      fetchEntries();
    } catch (error) {
      console.error("Failed to like:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add New Entry */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          Ins Gästebuch schreiben
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            placeholder="Dein Name"
            value={newEntry.name}
            onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })}
            className="rounded-xl"
          />
          <Textarea
            placeholder="Deine Nachricht..."
            value={newEntry.message}
            onChange={(e) => setNewEntry({ ...newEntry, message: e.target.value })}
            rows={3}
            className="rounded-xl"
          />
          <Button type="submit" disabled={submitting} className="w-full rounded-xl bg-primary hover:bg-primary/90">
            <Send className="w-4 h-4 mr-2" />
            {submitting ? "Wird gesendet..." : "Absenden"}
          </Button>
        </form>
      </div>

      {/* Entries List */}
      <div className="space-y-4">
        {entries.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Noch keine Einträge. Sei der Erste!</p>
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry.id} className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{entry.author_name || entry.name}</p>
                  <p className="text-xs text-gray-400">
                    {entry.created_at && formatDistanceToNow(new Date(entry.created_at), { addSuffix: true, locale: de })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => likeEntry(entry.id)}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500"
                  >
                    <Heart className={`w-4 h-4 ${entry.likes > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                    {entry.likes || 0}
                  </button>
                  {isOwner && (
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-3 text-gray-700 whitespace-pre-line">{entry.message}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
